import React, { useState, useEffect } from 'react';
import { getDashboard, getDashboardTendances, getDashboardObjectifs } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Chart from 'react-apexcharts';
import { FiUsers, FiActivity, FiDollarSign, FiTarget } from 'react-icons/fi';

const MOIS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

const Dashboard: React.FC = () => {
    const { user } = useAuth();
    const [data, setData] = useState<any>(null);
    const [tendances, setTendances] = useState<any[]>([]);
    const [objectifs, setObjectifs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getDashboard(),
            getDashboardTendances(new Date().getFullYear()),
            getDashboardObjectifs()
        ]).then(([d, t, o]) => {
            setData(d.data);
            setTendances(t.data.map((m: any) => ({ ...m, mois_label: MOIS[m.mois - 1] })));
            setObjectifs(o.data);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading">Chargement...</div>;
    if (!data) return <div className="error">Erreur de chargement</div>;

    const kpis = [
        { label: 'Commerciaux actifs', value: data.commerciaux, icon: <FiUsers />, color: '#16A34A' },
        { label: 'Clients', value: data.clients, icon: <FiUsers />, color: '#22C55E' },
        { label: 'Activites du jour', value: data.activites_jour, icon: <FiActivity />, color: '#F59E0B' },
        { label: 'Activites du mois', value: data.activites_mois, icon: <FiActivity />, color: '#14532D' },
        { label: 'CA du mois', value: `${(data.ca_mois || 0).toLocaleString()} F`, icon: <FiDollarSign />, color: '#22C55E' },
        { label: 'Taux objectifs', value: `${Math.round(data.taux_objectif_moyen || 0)}%`, icon: <FiTarget />, color: '#16A34A' },
    ];

    const lineOpts: ApexCharts.ApexOptions = {
        chart: { type: 'line', toolbar: { show: true }, fontFamily: 'DM Sans, sans-serif' },
        stroke: { width: [3, 3], curve: 'smooth' },
        colors: ['#16A34A', '#3B82F6'],
        xaxis: { categories: tendances.map(t => t.mois_label) },
        yaxis: [
            { title: { text: 'Activites' }, labels: { formatter: (v: number) => Math.round(v).toString() } },
            { opposite: true, title: { text: 'CA (F)' }, labels: { formatter: (v: number) => v.toLocaleString() } }
        ],
        legend: { position: 'top' },
        tooltip: { shared: true, intersect: false },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
    };

    const lineSeries = [
        { name: 'Activites', data: tendances.map(t => t.nb_activites || 0) },
        { name: 'CA (F)', data: tendances.map(t => t.ca || 0) }
    ];

    const pieOpts: ApexCharts.ApexOptions = {
        chart: { type: 'donut', fontFamily: 'DM Sans, sans-serif' },
        labels: data.activites_par_type?.map((a: any) => a.type) || [],
        colors: ['#16A34A', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#14B8A6'],
        legend: { position: 'bottom' },
        plotOptions: { pie: { donut: { size: '55%', labels: { show: true, total: { show: true, label: 'Total' } } } } },
        dataLabels: { enabled: true, formatter: (val: number) => `${Math.round(val)}%` },
    };

    const pieSeries = data.activites_par_type?.map((a: any) => a.total) || [];

    const barOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
        colors: ['#16A34A'],
        xaxis: { labels: { formatter: (v: string) => Number(v).toLocaleString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        tooltip: { y: { formatter: (v: number) => `${v.toLocaleString()} F` } },
        dataLabels: { enabled: false },
    };

    const topCommerciaux = data.top_commerciaux?.slice(0, 8) || [];
    const barSeries = [{ name: 'CA (F)', data: topCommerciaux.map((c: any) => ({ x: c.nom, y: c.ca || 0 })) }];

    return (
        <div className="dashboard">
            <div className="page-header">
                <h2>Tableau de bord</h2>
                <p>Bienvenue, {user?.prenom} {user?.nom}</p>
            </div>

            <div className="kpi-grid">
                {kpis.map((kpi, i) => (
                    <div key={i} className="kpi-card" style={{ borderLeftColor: kpi.color }}>
                        <div className="kpi-icon" style={{ color: kpi.color }}>{kpi.icon}</div>
                        <div className="kpi-content">
                            <span className="kpi-value">{kpi.value}</span>
                            <span className="kpi-label">{kpi.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="charts-grid">
                <div className="chart-card">
                    <h3>Evolution mensuelle</h3>
                    <Chart options={lineOpts} series={lineSeries} type="line" height={300} />
                </div>

                <div className="chart-card">
                    <h3>Activites par type</h3>
                    <Chart options={pieOpts} series={pieSeries} type="donut" height={300} />
                </div>

                <div className="chart-card">
                    <h3>Top commerciaux (CA)</h3>
                    <Chart options={barOpts} series={barSeries} type="bar" height={300} />
                </div>

                <div className="chart-card">
                    <h3>Objectifs en cours</h3>
                    <div className="objectifs-list">
                        {objectifs.length === 0 ? <p className="text-muted">Aucun objectif en cours</p> :
                            objectifs.map((obj: any, i: number) => (
                                <div key={i} className="objectif-item">
                                    <div className="objectif-header">
                                        <span className="badge">{obj.type_activite || 'Global'}</span>
                                        <span className="objectif-periode">{obj.periode}</span>
                                    </div>
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{
                                            width: `${Math.min(100, obj.taux || 0)}%`,
                                            backgroundColor: (obj.taux || 0) >= 100 ? '#22C55E' : (obj.taux || 0) >= 50 ? '#F59E0B' : '#EF4444'
                                        }}></div>
                                    </div>
                                    <div className="objectif-footer">
                                        <span>{obj.valeur_realisee} / {obj.valeur_cible}</span>
                                        <span style={{ fontWeight: 'bold' }}>{obj.taux || 0}%</span>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
